import { lazy, Suspense, useMemo } from "react";
import { useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import PageShimmer from "../components/UniversalComponent/loaders/PageShimmer";
import ReduxModal from "../components/UniversalComponent/model/ReduxModel";
import TyreLoader from "../components/UniversalComponent/loaders/TyreLoader";
import { Paths } from "../constants/enums";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import RedirectToRoleDashboard from "./RedirectToRoleDashboard";
import { roleBasedRoutes } from "./roleBasedRoutes";
import { getAllowedRoles } from "./routePermissions";

// Public route components (lazy loaded)
const Login = lazy(() => import("../components/auth/Login"));
const Page = lazy(() => import("../components/Common/page"));
const CustomerOnboard = lazy(() =>
  import("../components/customer/CustomerOnboard")
);
const CustomerUpdateWrapper = lazy(() =>
  import("../components/customer/CustomerUpdateWrapper")
);
const RegistrationSuccess = lazy(() =>
  import("../components/customer/RegistrationSuccess")
);

// Lazy loaded components mapping
const componentMap = {
  Dashboard: lazy(() => import("../components/dashboard/Dashboard/Dashboard")),
  Payments: lazy(() => import("../components/payment/Payments")),
  ApprovalCustomer: lazy(() =>
    import("../components/customer/ApprovalCustomer")
  ),
  CustomerRegistration: lazy(() =>
    import("../components/NewCustomerRegistration/CustomerRegistration")
  ),
  CustomerDashboard: lazy(() =>
    import("../components/CustomerDashbaord/CustomerDashboard")
  ),
  InvoiceCustomer: lazy(() =>
    import(
      "../components/CustomerDashbaord/InvoiceCustomerComponent/InvoiceCustomer"
    )
  ),
  PaymentsCustomer: lazy(() =>
    import(
      "../components/CustomerDashbaord/UpdatePaymentComponentDetails/PaymentsCustomer"
    )
  ),
  LogisticDetails: lazy(() =>
    import(
      "../components/CustomerDashbaord/LogisticDetailsComponent/LogisticDetails"
    )
  ),
  DisputeDetails: lazy(() =>
    import("../components/CustomerDashbaord/DisputeManagement/DisputeDetails")
  ),
  CreditLineRequest: lazy(() =>
    import(
      "../components/CustomerDashbaord/CreditLineRequest/CreditLineRequest"
    )
  ),
  AdminDashboard: lazy(() => import("../components/Admin/AdminDashboard")),
  Role: lazy(() => import("../components/Admin/Role")),
  Department: lazy(() => import("../components/Admin/Department")),
  Designation: lazy(() => import("../components/Admin/Designation")),
  Employee: lazy(() => import("../components/Admin/Employee")),
  BusinessUnit: lazy(() =>
    import("../components/Admin/BusinessUnitComponent/BusinessUnit")
  ),
  CustomerType: lazy(() => import("../components/Admin/CustomerType")),
  CustomerRegion: lazy(() => import("../components/Admin/CustomerRegion")),
  CustomerSubRegion: lazy(() =>
    import("../components/Admin/CustomerSubRegion")
  ),
  RaisedDispute: lazy(() =>
    import("../components/Raised-Dispute/RaisedDispute")
  ),
  ViewAccountStatment: lazy(() =>
    import(
      "../components/CustomerDashbaord/ViewAccountStatment/ViewAccountStatment"
    )
  ),
  UserProfile: lazy(() => import("../components/userProfile/UserProfile")),
  OnboardedCustomers: lazy(() =>
    import("../components/Admin/OnboardedCustomers/OnboardedCustomers")
  ),
  CustomerTransactionInvoiceTable: lazy(() =>
    import(
      "../components/Admin/CustomerTransactionInvoice/CustomerTransactionInvoiceTable"
    )
  ),
  LevelsManagementPage: lazy(() =>
    import("../components/Admin/LevelAssignment/LevelsManagementPage")
  ),
  CreditLineCustomer: lazy(() =>
    import("../components/customer/CreditLineCustomer")
  ),
  CreditLineDetails: lazy(() =>
    import("../components/CreditLineDetails/CreditLineDetails")
  ),
  UserGuideList: lazy(() => import("../components/UserGuide/UserGuideList")),
  CustomersHierarchy: lazy(() =>
    import("../components/Admin/CustomersHerarchy/CustomersHerarchy")
  ),
  AccountStatementDetails: lazy(() =>
    import("../components/dashboard/AccountStatement/AccountStatementDetails")
  ),
  ExecutiveDashboard: lazy(() =>
    import("../components/ExecutiveDashboard/ExecutiveDashboard")
  ),
  ViewPaymentDetails: lazy(() =>
    import("../components/dashboard/Paymentdetails/ViewPaymentDetails")
  ),
  ViewLogisticDetails: lazy(() =>
    import("../components/dashboard/LogisticDetails/ViewLogisticDetails")
  ),
  ViewInvoiceAndCreditNoteDetails: lazy(() =>
    import(
      "../components/dashboard/InvoiceAndCreditNotedetails/ViewInvoiceAndCreditNoteDetails"
    )
  ),
  DisputeLevelsManagementPage: lazy(() =>
    import(
      "../components/Admin/DisputeLevelsManagement/DisputeLevelsManagementPage"
    )
  ),
  UnconfiguredRolePage: lazy(() =>
    import("../components/Common/UnConfigured/UnconfiguredRolePage")
  ),
  ViewCustomerPriceList: lazy(() =>
    import("../components/dashboard/PriceList/ViewCustomerPriceList")
  ),
  ViewCustomerBackOrder: lazy(() =>
    import("../components/dashboard/BackOrder/ViewCustomerBackOrder")
  ),
  CustomerLevelHerarchy: lazy(() =>
    import("../components/Admin/CustomersHerarchy/CustomerLevelHerarchy")
  ),
  BulkUpload: lazy(() => import("../components/BulkUpload/BulkUpload")),
  CustomerFeedbackTable: lazy(() =>
    import("../components/Feedback/CustomerFeedbackTable")
  ),
  Privilege: lazy(() => import("../components/Admin/privilege")),
  Promotion: lazy(() =>
    import("../components/Admin/Promotion/MailPromotionTable")
  ),
  UserTable: lazy(() =>
    import("../components/Admin/PrivilegeComponents/UserTable")
  ),
  RoleTables: lazy(() =>
    import("../components/Admin/PrivilegeComponents/RoleTables")
  ),
};

const FullPageLoader = () => (
  <div className="flex justify-center items-center h-screen">
    <TyreLoader />
  </div>
);

const AppRoutes = () => {
  const { role } = useSelector((state) => state.auth);

  // Get current role's routes — preserves correct component per role
  const currentRoleRoutes = useMemo(
    () => roleBasedRoutes[role] || [],
    [role]
  );

  return (
    <>
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route
            path={Paths.ROOT}
            element={
              <PublicRoute
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <Login />
                  </Suspense>
                }
              />
            }
          />
          <Route
            path={Paths.ADD_CUSTOMER}
            element={
              <PublicRoute
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <CustomerOnboard />
                  </Suspense>
                }
              />
            }
          />
          <Route
            path={Paths.UPDATE_CUSTOMER}
            element={
              <PublicRoute
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <CustomerUpdateWrapper />
                  </Suspense>
                }
              />
            }
          />
          <Route
            path={Paths.REGISTRATION_SUCCESS}
            element={
              <PublicRoute
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <RegistrationSuccess />
                  </Suspense>
                }
              />
            }
          />

          {/* Private Routes — role-specific components with real RBAC via allowedRoles */}
          {role && (
            <Route
              path={Paths.ROOT}
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <Page />
                </Suspense>
              }
            >
              {currentRoleRoutes.map(({ path, component }) => {
                const LazyComponent = componentMap[component];
                if (!LazyComponent) return null;

                return (
                  <Route
                    key={path}
                    path={path + "/*"}
                    element={
                      <PrivateRoute
                        allowedRoles={[...getAllowedRoles(path)]}
                        element={
                          <Suspense fallback={<PageShimmer />}>
                            <LazyComponent />
                          </Suspense>
                        }
                      />
                    }
                  />
                );
              })}
            </Route>
          )}

          <Route
            path={Paths.WILDCARD}
            element={
              <PublicRoute
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <RedirectToRoleDashboard />
                  </Suspense>
                }
              />
            }
          />
        </Routes>
      </Suspense>

      <ToastContainer />
      <ReduxModal />
    </>
  );
};

export default AppRoutes;
